'use client';
import { Cpu } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { modelIconSlug } from '../../lib/modelIcon';
import { AdaptiveBrandMark } from './AdaptiveBrandMark';

// Raster brand assets use their real primary format, avoiding a guaranteed SVG 404.
const WEBP_PRIMARY = new Set(['xiaomimimo-color', 'minimax-account-color']);

/** Brand icon for a model, resolved from its name/exec string via lobe-icons.
 *  Monochrome variants use a currentColor mask, so the same asset reads on every local surface rather than
 *  guessing from the root skin and disappearing inside a light popover or a dark rail. */
export function ModelIcon({ name, size = 20, className = '' }: { name?: string | null; size?: number; className?: string }) {
  const icon = modelIconSlug(name);
  const [fallback, setFallback] = useState(false);
  const [failed, setFailed] = useState(false);
  // Reset per icon so a name change from a missing icon to a good one re-tries the asset.
  useEffect(() => { setFallback(false); setFailed(false); }, [icon?.slug]);

  const onError = useCallback(() => {
    // color icon: svg missing → try the webp raster; anything else missing → give up on the <img>
    // and render the generic glyph so there's no broken image (and the 404 loop stops).
    if (icon?.color && !fallback && !WEBP_PRIMARY.has(icon.slug)) setFallback(true);
    else setFailed(true);
  }, [icon?.color, icon?.slug, fallback]);

  if (!icon || failed) return <Cpu size={size} className={`text-muted-foreground ${className}`} aria-hidden />;

  if (!icon.color) {
    return <AdaptiveBrandMark src={`/models/${icon.slug}.svg`} size={size} monochrome className={className} />;
  }

  const ext = WEBP_PRIMARY.has(icon.slug) || fallback ? 'webp' : 'svg';
  return (
    <AdaptiveBrandMark
      src={`/models/${icon.slug}.${ext}`}
      size={size}
      className={className}
      onError={onError}
    />
  );
}
